import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toggleEditLegend, decrementSteps, incrementSteps, setMaxValue, setMinValue, setSteps } from '../../legends';
import { updateFieldViewUrlForImage, storeFieldViewLegendForImage } from 'imagery';
import {
  getActiveFieldUuid,
  getSelectedOverlayLayer,
  getSelectedOverlayUuid
} from 'current/selectors';
import { getUnitsOfMeasureTemplate, getUsernameForFVPlusSpatial } from 'user/selectors';
import { updateLegend } from '../../legends/loaders';
import EditLegend from './EditLegend';
import { UNITS_OF_MEASURE } from 'constants/unitsOfMeasure';
import { getMapLayerUnitKey, getValuesForUpdate } from 'util/legend';

const EditLegendContainer = ({
  decrementSteps,
  incrementSteps,
  legend,
  steps,
  maxValue,
  minValue,
  toggleEditLegend,
  setMaxValue,
  setMinValue,
  setSteps,
  cropCurrentMapLayer,
  fieldUuid,
  mapType,
  username,
  isMetric,
  updateFieldViewUrlForImage,
  storeFieldViewLegendForImage,
  overlayImageUuid,
  cropNominalWeight
}) => {
  const { isEditLegendVisible } = legend;
  const unitKey = getMapLayerUnitKey(mapType);
  const [isFormInvalid, setisFormInvalid] = useState(false);

  useEffect(() => {
    const cacheBuster = `${maxValue}_${minValue}_${steps}`;
    updateFieldViewUrlForImage({fieldUuid, overlayUuid: overlayImageUuid, layer: mapType, cacheBuster});
    setMaxValue(maxValue);
    setMinValue(minValue);
    setSteps(steps);
    setisFormInvalid(false);
  },[isEditLegendVisible]);

  const setMax = (event) => {
    let valueToInt;
    const isInvalidInt = isNaN(parseInt(event.target.value, 10));
    // USDEV2-92: Allow for a negative sign but not negative decimals where the decimal is 0 < decimal > -1
    if(isInvalidInt && !event.target.value.includes('.')) {
      valueToInt = event.target.value;
    } else {
      valueToInt = parseInt(event.target.value, 10) || 0;
    }
    setisFormInvalid(isInvalidInt || valueToInt <= parseInt(legend.minValue, 10));
    setMaxValue(valueToInt);
  };

  const setMin = (event) => {
    let valueToInt;
    const isInvalidInt = isNaN(parseInt(event.target.value, 10));
    if(isInvalidInt && !event.target.value.includes('.')) {
      valueToInt = event.target.value;
    } else {
      valueToInt = parseInt(event.target.value, 10) || 0;
    }
    setisFormInvalid(isInvalidInt || valueToInt >= parseInt(legend.maxValue, 10));
    setMinValue(valueToInt);
  };

  const saveLegend = () => {
    const valuesForUpdate = getValuesForUpdate({ legend, unitKey, cropNominalWeight });

    updateLegend({
      username,
      fieldUuid: fieldUuid.toUpperCase(),
      crop: cropCurrentMapLayer,
      metric: isMetric,
      mapType,
      maxValue: valuesForUpdate.maxValue,
      minValue: valuesForUpdate.minValue,
      steps: legend.steps
    })
    .then( () => {
      // USDEV2-92: reset image legend after update so that the updated overlay legend will be loaded
      const cacheBuster = `${legend.maxValue}_${legend.minValue}_${legend.steps}`;
      updateFieldViewUrlForImage({
        fieldUuid,
        overlayUuid: overlayImageUuid,
        layer: mapType,
        cacheBuster
      });
      storeFieldViewLegendForImage({ fieldUuid, overlayUuid: overlayImageUuid, legend: null, layer: mapType });
      toggleEditLegend();
    });
  };

  return isEditLegendVisible ? <EditLegend
    decrementSteps={decrementSteps}
    incrementSteps={incrementSteps}
    isFormInvalid={isFormInvalid}
    legend={legend}
    onCancel={toggleEditLegend}
    onSave={saveLegend}
    setMax={setMax}
    setMin={setMin} /> : null;
};

EditLegendContainer.defaultPropTypes = {
  legend: {
    maxValue: 1,
    minValue: 0,
    steps: 3
  }
};

EditLegendContainer.propTypes = {
  cropCurrentMapLayer: PropTypes.string,
  decrementSteps: PropTypes.func,
  fieldUuid: PropTypes.string,
  incrementSteps: PropTypes.func,
  isEditLegendVisible: PropTypes.bool,
  isMetric: PropTypes.bool,
  legend: PropTypes.object,
  mapType: PropTypes.string,
  maxValue: PropTypes.number,
  minValue: PropTypes.number,
  overlayImageUuid: PropTypes.string,
  setMaxValue: PropTypes.func,
  setMinValue: PropTypes.func,
  setSteps: PropTypes.func,
  steps: PropTypes.number,
  storeFieldViewLegendForImage: PropTypes.func,
  toggleEditLegend: PropTypes.func,
  updateFieldViewUrlForImage: PropTypes.func,
  username: PropTypes.string,
  cropNominalWeight: PropTypes.number
};

function mapStateToProps(state) {
  return {
    legend: state.legends,
    mapType: getSelectedOverlayLayer(state),
    fieldUuid: getActiveFieldUuid(state),
    cropCurrentMapLayer: state.current.cropCurrentMapLayer,
    username: getUsernameForFVPlusSpatial(state),
    isMetric: getUnitsOfMeasureTemplate(state) === UNITS_OF_MEASURE.si,
    overlayImageUuid: getSelectedOverlayUuid(state)
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    decrementSteps,
    incrementSteps,
    toggleEditLegend,
    setMaxValue,
    setMinValue,
    setSteps,
    updateFieldViewUrlForImage,
    storeFieldViewLegendForImage
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(EditLegendContainer);
